import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  useTheme,
  Alert,
  CircularProgress,
  Container,
  alpha,
  InputAdornment,
  IconButton,
  Fade,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { ADMIN_COOKIE_KEY, USER_ROLE_KEY, USER_ID_KEY, USER_FULL_ACCESS_KEY } from '../../hooks/useAdmin';
import { useThemeMode } from '../../App';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LoginIcon from '@mui/icons-material/Login';
import { styled } from '@mui/material/styles';
import LogoImg from '../../logob.png';

const API_URL = 'https://guide.zerdegroup.kz/api/';

const Logo = styled('img')(({ theme }) => ({
  height: '50px',
  width: 'auto',
  marginBottom: theme.spacing(3),
  filter: theme.palette.mode === 'dark' ? 'brightness(1.2)' : 'none',
}));

const AuthContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(45deg, ${alpha(theme.palette.primary.dark, 0.9)}, ${alpha(theme.palette.secondary.dark, 0.9)})`,
    zIndex: -1,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `radial-gradient(circle at 30% 107%, ${alpha('#fff', 0.05)} 0%, ${alpha('#fff', 0)} 70%)`,
    zIndex: -1,
  },
}));

const AuthPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: '100%',
  maxWidth: 450,
  borderRadius: 16,
  boxShadow: theme.palette.mode === 'light'
    ? '0 10px 30px rgba(0,0,0,0.1)'
    : '0 10px 30px rgba(0,0,0,0.3)',
  backdropFilter: 'blur(10px)',
  background: alpha(theme.palette.background.paper, 0.9),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease',
  overflow: 'hidden',
}));

const AuthButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.2),
  borderRadius: 10,
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: theme.palette.mode === 'light'
    ? '0 4px 12px rgba(0,0,0,0.1)'
    : '0 4px 12px rgba(0,0,0,0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 6px 16px rgba(0,0,0,0.15)'
      : '0 6px 16px rgba(0,0,0,0.4)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.8),
    },
    '&.Mui-focused': {
      backgroundColor: alpha(theme.palette.background.paper, 1),
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
    }
  },
}));

const Auth: React.FC = () => {
  const theme = useTheme();
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ login, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Ошибка при входе');
      }

      // Сохраняем токен и данные пользователя в куки
      Cookies.set(ADMIN_COOKIE_KEY, data.token, { expires: 7 });
      Cookies.set(USER_ROLE_KEY, data.user.role, { expires: 7 });
      Cookies.set(USER_ID_KEY, data.user.id.toString(), { expires: 7 });
      Cookies.set(USER_FULL_ACCESS_KEY, data.user.full_access.toString(), { expires: 7 });
      
      // Если роль не admin, запрещаем доступ
      // if (data.user.role !== 'admin') {
      //   throw new Error('Доступ запрещен. Требуются права администратора.');
      // }

      navigate('/admin/panel');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && login && password) {
      e.preventDefault();
      handleLogin();
    }
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <AuthContainer className="fade-in">
      <AuthPaper elevation={0}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Logo src={LogoImg} alt="Zerde Guide" />
          <Typography 
            variant="h5" 
            component="h1" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              color: theme.palette.mode === 'dark' ? 'white' : 'text.primary',
              mb: 0.5
            }}
          >
            Вход в админ-панель
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Введите учетные данные для доступа
          </Typography>
        </Box>

        <Fade in={!!error}>
          <Box sx={{ mb: error ? 3 : 0 }}>
            {error && (
              <Alert 
                severity="error" 
                variant="filled"
                sx={{ 
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              >
                {error}
              </Alert>
            )}
          </Box>
        </Fade>

        <Box 
          component="form" 
          onSubmit={(e) => { 
            e.preventDefault(); 
            if (!isLoading && login && password) {
              handleLogin();
            }
          }}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <StyledTextField
            fullWidth
            label="Логин"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            variant="outlined"
            disabled={isLoading}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineOutlinedIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <StyledTextField
            fullWidth
            label="Пароль"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outlined"
            disabled={isLoading}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePassword}
                    edge="end"
                    size="small"
                    tabIndex={-1}
                  >
                    {showPassword ? <VisibilityOff color="action" /> : <Visibility color="action" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Box sx={{ mt: 2 }}>
            <AuthButton
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              type="submit"
              disabled={isLoading || !login || !password}
              startIcon={isLoading ? undefined : <LoginIcon />}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Войти'}
            </AuthButton>
          </Box>
        </Box>
      </AuthPaper>
    </AuthContainer>
  );
};

export default Auth; 